import { hashCode, ensureArray } from '.';
/**
 * Creates sort callback which can be used for `array.sort` input.
 * A custom key function can be supplied to customize the sort order.
 * Key can return any nested data structure, supports number, booleans, arrays.
 *
 * @example
 * ```
 * // Sort by object key
 * arr.sort(sortByCb(v => v.myKey))
 * // Sort by number in reversed order
 * arr.sort(sortByCb(v => v.numberKey * -1))
 * // Sort by object key, then by boolean value in revered order
 * arr.sort(sortByCb(v => [v.myKey, !v.boolValue])
 * ```
 * @category Array
 * @param key a function to execute to decide the order
 * @returns `array.sort` compatible callback
 */
export function sortByCb(key) {
    if (key === void 0) { key = function (v) { return v; }; }
    return function (a, b) {
        var doCompare = function (keyA, keyB) {
            var _a;
            if (typeof keyA === 'number' && typeof keyB === 'number') {
                return keyA < keyB ? -1 : keyA > keyB ? 1 : 0;
            }
            if (typeof keyA === 'boolean' && typeof keyB === 'boolean') {
                return keyA < keyB ? -1 : keyA > keyB ? 1 : 0;
            }
            if (keyA instanceof Date && keyB instanceof Date) {
                return doCompare(keyA.getTime(), keyB.getTime());
            }
            if (keyA instanceof Array && keyB instanceof Array) {
                var res = keyA
                    .map(function (v, i) { return doCompare(v, keyB[i]); })
                    .filter(function (v) { return v !== 0; });
                return (_a = res[0]) !== null && _a !== void 0 ? _a : 1;
            }
            var stringA = String(keyA);
            var stringB = String(keyB);
            return stringA < stringB ? -1 : stringA > stringB ? 1 : 0;
        };
        return doCompare(key(a), key(b));
    };
}
export function sortBy(arr, key) {
    if (key === void 0) { key = function (v) { return v; }; }
    return arr
        .map(function (v, i) { return [v, i]; })
        .sort(sortByCb(function (_a) {
        var v = _a[0], i = _a[1];
        return [key(v), i];
    }))
        .map(function (v) { return v[0]; });
}
export function generateRange(start, stop, step) {
    if (stop === undefined) {
        stop = start;
        start = 0;
    }
    if (step === undefined) {
        step = 1;
    }
    if ((step > 0 && start >= stop) || (step < 0 && start <= stop)) {
        return [];
    }
    var result = [];
    for (var i = start; step > 0 ? i < stop : i > stop; i += step) {
        result.push(i);
    }
    return result;
}
export function flatMap(array, callback) {
    return array.reduce(function (acc, v, index) { return acc.concat(callback(v, index, array)); }, []);
}
export function flatten(array, depth) {
    var d = depth !== null && depth !== void 0 ? depth : 1;
    if (d > 0) {
        return array.reduce(function (acc, val) {
            return acc.concat(val instanceof Array ? flatten(val, d - 1) : val);
        }, []);
    }
    return array.slice();
}
export function chunk(array, size) {
    return array.reduce(function (acc, _, i) {
        if (i % size === 0) {
            acc.push(array.slice(i, i + size));
        }
        return acc;
    }, []);
}
export function groupBy(array, keyCallback) {
    return Object.values(array.reduce(function (prev, cur) {
        var _a;
        var key = keyCallback(cur);
        var keyHash = hashCode(keyCallback(cur));
        if (keyHash in prev) {
            (_a = prev[keyHash]) === null || _a === void 0 ? void 0 : _a[1].push(cur);
        }
        else {
            prev[keyHash] = [key, [cur]];
        }
        return prev;
    }, {}));
}
export function indexBy(array, keyCallback) {
    return array.reduce(function (prev, cur) {
        return ensureArray(keyCallback(cur)).reduce(function (prev2, key) {
            var _a;
            if (key in prev) {
                (_a = prev2[key]) === null || _a === void 0 ? void 0 : _a.push(cur);
            }
            else {
                prev2[key] = [cur];
            }
            return prev;
        }, prev);
    }, {});
}
export function deduplicate(array) {
    return deduplicateBy(array, function (v) { return v; });
}
export function deduplicateBy(array, key) {
    var prims = {
        boolean: {},
        number: {},
        string: {}
    };
    var objs = [];
    return array.filter(function (rawItem) {
        var item = key(rawItem);
        var type = typeof item;
        if (type in prims) {
            if (item in prims[type]) {
                return false;
            }
            else {
                return (prims[type][item] = true);
            }
        }
        else {
            return objs.indexOf(item) !== -1 ? false : objs.push(item);
        }
    });
}
export function difference(array, values, key) {
    if (key === void 0) { key = function (v) { return v; }; }
    return array.filter(function (v) { return !values.some(function (v2) { return key(v) === key(v2); }); });
}
export function intersection(values, key) {
    if (key === void 0) { key = function (v) { return v; }; }
    var first = values[0], rest = values.slice(1);
    return first
        ? first.filter(function (v) {
            return rest.every(function (v2) { return v2.some(function (v3) { return key(v) === key(v3); }); });
        })
        : [];
}
export function union(values, key) {
    if (key === void 0) { key = function (v) { return v; }; }
    return deduplicateBy(values.reduce(function (prev, cur) {
        prev.push.apply(prev, cur);
        return prev;
    }, []), key);
}
/**
 * Checks if any value from provided values array is included in the provided array using provided `comparator` function (or by default comparing using `===`).
 *
 * @example
 * ```
 * includesAny([1, 2, 3], [3, 4, 5])
 * // true
 * ```
 * @category Array
 */
export function includesAny(array, values, key) {
    if (key === void 0) { key = function (v) { return v; }; }
    return values.some(function (v) { return array.some(function (v2) { return key(v2) === key(v); }); });
}
/**
 * Checks if all values from provided values array are included in the provided array using provided `comparator` function (or by default comparing using `===`).
 *
 * @example
 * ```
 * includesAll([1, 2, 3], [1, 2])
 * // true
 * ```
 * @category Array
 */
export function includesAll(array, values, key) {
    if (key === void 0) { key = function (v) { return v; }; }
    return values.every(function (v) { return array.some(function (v2) { return key(v2) === key(v); }); });
}
/**
 * Returns the index of the first element in the array where predicate is true, and -1
 * otherwise.
 *
 * @example
 * ```
 * const obj = [{name: 'abc'}]
 * const index = findIndex(obj, v => v.name = 'abc')
 * // 0
 * ```
 * @param predicate find calls predicate once for each element of the array, in ascending
 * order, until it finds one where predicate returns true. If such an element is found,
 * findIndex immediately returns that element index. Otherwise, findIndex returns -1.
 * @category Array
 */
export function findIndex(array, compare) {
    for (var index = 0; index < array.length; index++) {
        if (compare(array[index])) {
            return index;
        }
    }
    return -1;
}
